import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type UserFormValues } from '.';
import { type UserSpecsFragment } from '../../../../api/fragments/UserSpecs';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import RoleSelectField from '../../../../components/fields/RoleSelectField';
import UserGroupSelectField from '../../../../components/fields/UserGroupSelectField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';

const colSpan = { lg: 8, md: 12, xs: 24 };

export type UserDetailFormProps = {
    disabled?: boolean;
    user?: UserSpecsFragment;
    updated?: string;
    offset?: string;
};

const UserDetailForm = ({ disabled = false, updated, offset }: UserDetailFormProps) => {
    const { t } = useTranslation('userDetails');
    const { initialValues, setFieldValue } = useFormikContext<UserFormValues>();

    const company = useCompany(true);

    const { yesNoSwitch } = useSystemSwitchData();

    useEffect(() => {
        if (initialValues.roleIds.length === 0) {
            setFieldValue('roleIds', []);
        }

        if (initialValues.userGroupIds.length === 0) {
            setFieldValue('userGroupIds', []);
        }
    }, [initialValues.roleIds, setFieldValue, company, initialValues.userGroupIds]);

    return (
        <CollapsibleWrapper defaultActiveKey={t('userDetails:cardTitles.configuration')}>
            <Panel key={t('userDetails:cardTitles.configuration')} header={t('userDetails:cardTitles.configuration')}>
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <FormFields.InputField
                            {...t('userDetails:fields.displayName', { returnObjects: true })}
                            disabled={disabled}
                            name="displayName"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.PhoneAndPrefixField
                            {...t('userDetails:fields.mobile', { returnObjects: true })}
                            name="mobile"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputField
                            disabled={disabled}
                            {...t('userDetails:fields.email', { returnObjects: true })}
                            name="email"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <RoleSelectField
                            {...t('userDetails:fields.roles', { returnObjects: true })}
                            disabled={disabled}
                            mode="multiple"
                            name="roleIds"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <UserGroupSelectField
                            {...t('userDetails:fields.userGroups', { returnObjects: true })}
                            disabled={disabled}
                            mode="multiple"
                            name="userGroupIds"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputField
                            {...t('userDetails:fields.alias', { returnObjects: true })}
                            disabled={disabled}
                            name="alias"
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('userDetails:fields.isActive', { returnObjects: true })}
                            name="isActive"
                            {...yesNoSwitch}
                        />
                    </Col>
                    {updated && (
                        <Col {...colSpan}>
                            <FormFields.DisplayFieldWithUTCOffset
                                {...t('common:fields.updated', { returnObjects: true, offset })}
                                value={updated}
                            />
                        </Col>
                    )}
                </Row>
            </Panel>
        </CollapsibleWrapper>
    );
};

export default UserDetailForm;
